// Version 1 — Cost analysis card.
//
// Sits at the top of the Version 1 tab. Computes annual fully-loaded cost
// using the model's `costPerRole` rates, with shore-aware lookups so each FTE
// is priced against the right rate column (onshore / nearshore / offshore).
//
// Modeled cost
//   Sums the engine's per-cell shore FTE and cost
//   (`onshoreFte`/`nearshoreFte`/`offshoreFte` and the matching cost fields)
//   from the cells the tab passes in — the modeled grid with any manual cell
//   adjustments already applied. Those reflect the scenario's per-discipline OGS
//   configuration (hub, %, selected levels) and the hub-aware cost rates, so the
//   numbers match the FTE table on the tab.
//
// Actual cost (when a Current Staff Plan is linked)
//   For each StaffPlan cell, look up `cost(onshore, ...)` for the onshore FTE
//   and `cost(plan.hubLocation.shore, ...)` for the hub FTE. If the plan has
//   no hub location, the hub leg defaults to `offshore` so legacy plans still
//   produce a sensible number.
//
// All costing here happens in the model's base currency — `lookupCostPerRole`
// returns base-currency rates. Conversion to the viewer's display currency is
// applied once, by `money()` at render time; never to the rates themselves.

import { useMemo } from 'react';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Coins, Minus } from 'lucide-react';
import { lookupCostPerRole, resolveCellSplit, resolveRowHub } from '@rc/engine';
import type {
  DisciplineChannelCell,
  Model,
  StaffPlan,
} from '@rc/engine/types';
import { useCurrency } from '../../lib/currency';

interface Props {
  model: Model;
  /** The modeled grid with the scenario's manual adjustments applied. */
  cells: DisciplineChannelCell[];
  /** Whether any of those cells were adjusted by hand, so the panel can name
   *  the team it is pricing. */
  adjusted?: boolean;
  staffPlan?: StaffPlan | null;
}

interface CostBreakdown {
  total: number;
  onshore: number;
  nearshore: number;
  offshore: number;
  fteTotal: number;
  fteOnshore: number;
  fteNearshore: number;
  fteOffshore: number;
}

const EMPTY: CostBreakdown = {
  total: 0,
  onshore: 0,
  nearshore: 0,
  offshore: 0,
  fteTotal: 0,
  fteOnshore: 0,
  fteNearshore: 0,
  fteOffshore: 0,
};

function buildModeledCost(cells: DisciplineChannelCell[]): CostBreakdown {
  const acc: CostBreakdown = { ...EMPTY };
  for (const c of cells || []) {
    const onFte = Number(c.onshoreFte) || 0;
    const nsFte = Number(c.nearshoreFte) || 0;
    const offFte = Number(c.offshoreFte) || 0;
    const fte = onFte + nsFte + offFte;
    if (fte <= 0) continue;

    acc.fteOnshore += onFte;
    acc.fteNearshore += nsFte;
    acc.fteOffshore += offFte;
    acc.fteTotal += fte;
    acc.onshore += Number(c.onshoreCost) || 0;
    acc.nearshore += Number(c.nearshoreCost) || 0;
    acc.offshore += Number(c.offshoreCost) || 0;
    acc.total += Number(c.totalCost) || 0;
  }
  return acc;
}

function buildActualCost(
  model: Model,
  staffPlan: StaffPlan | null | undefined,
): CostBreakdown {
  if (!staffPlan?.cells?.length) return { ...EMPTY };

  // Hubs that still exist in the model being costed. A staff plan authored
  // under a different model may reference hubs that are gone here — those are
  // ignored so costing falls back cleanly instead of pricing a dangling hub.
  const validHubIds = new Set((model.ogsHubs ?? []).map((h) => h.id));

  const acc: CostBreakdown = { ...EMPTY };
  for (const c of staffPlan.cells) {
    const split = resolveCellSplit(c);
    if (split.total <= 0) continue;

    // Hub roles sit at the row's chosen hub (falling back to the plan-wide
    // default, then offshore). The hub's shore + id drive the rate column so
    // costing follows wherever the row parks its Hub FTE.
    const rowHub = resolveRowHub(staffPlan, c.disciplineId, c.channelId, validHubIds);
    const hubShore = rowHub?.shore ?? 'offshore';
    const hubId = rowHub?.hubId;

    const onRate = lookupCostPerRole(model, 'onshore', c.disciplineId, c.channelId, c.levelId);
    const hubRate = lookupCostPerRole(
      model,
      hubShore,
      c.disciplineId,
      c.channelId,
      c.levelId,
      hubId,
    );

    const onCost = split.on * (Number(onRate) || 0);
    const hubCost = split.hub * (Number(hubRate) || 0);

    acc.fteOnshore += split.on;
    acc.fteTotal += split.total;
    acc.onshore += onCost;
    acc.total += onCost + hubCost;
    if (hubShore === 'nearshore') {
      acc.fteNearshore += split.hub;
      acc.nearshore += hubCost;
    } else {
      acc.fteOffshore += split.hub;
      acc.offshore += hubCost;
    }
  }
  return acc;
}

function blendedRate(b: CostBreakdown): number {
  return b.fteTotal > 0 ? b.total / b.fteTotal : 0;
}

function hubCost(b: CostBreakdown): number {
  return b.nearshore + b.offshore;
}
function hubFte(b: CostBreakdown): number {
  return b.fteNearshore + b.fteOffshore;
}

export default function CostAnalysisCard({
  model,
  cells,
  adjusted,
  staffPlan,
}: Props) {
  const modeled = useMemo(() => buildModeledCost(cells), [cells]);
  const actual = useMemo(() => buildActualCost(model, staffPlan), [model, staffPlan]);

  const hasModeled = modeled.fteTotal > 0;
  const hasActual = actual.fteTotal > 0;

  if (!hasModeled && !hasActual) {
    return (
      <section className="card border-dashed">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Coins size={16} className="text-slate-400" />
            <h3 className="text-base font-semibold">Cost analysis</h3>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Annual fully-loaded cost will appear here once the scenario has FTE
            output and the model has Cost-per-Role rates filled in.
          </p>
        </div>
      </section>
    );
  }

  const totalΔ = actual.total - modeled.total;
  const hubΔ = hubCost(actual) - hubCost(modeled);
  const onΔ = actual.onshore - modeled.onshore;

  return (
    <section className="card">
      <div className="card-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Coins size={16} className="text-brand-purple-500" />
              <h3 className="text-base font-semibold">Cost analysis</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Annual fully-loaded cost using the model's{' '}
              <span className="font-medium text-slate-600">Cost-per-Role</span>{' '}
              rates, applied per shore.{' '}
              {adjusted ? 'Modeled-Adjusted' : 'Modeled'} cost comes from the
              engine's per-cell shore split (the scenario's OGS configuration
              {adjusted ? ', with your manual cell edits applied' : ''}) priced at
              the matching rate column; actual cost prices the staff plan against
              the same rates, with each row's Hub roles costed at that row's
              selected hub
              {staffPlan?.hubLocation
                ? ` (default ${staffPlan.hubLocation.label || 'Hub'} · ${staffPlan.hubLocation.shore})`
                : ' (defaulting to offshore until set)'}
              .
            </p>
          </div>
          {hasActual && (
            <DeltaPill
              label="Total cost Δ"
              value={totalΔ}
              modeled={modeled.total}
              actual={actual.total}
            />
          )}
        </div>
      </div>
      <div
        className={clsx(
          'card-body',
          hasActual
            ? 'grid gap-4 grid-cols-1 lg:grid-cols-2'
            : 'space-y-4',
        )}
      >
        <CostRow
          title={adjusted ? 'Modeled-Adjusted' : 'Modeled'}
          subtitle={
            adjusted
              ? 'What the model prescribes, with your manual cell edits.'
              : 'What the model prescribes for this scenario.'
          }
          breakdown={modeled}
          accent="modeled"
        />
        {hasActual && (
          <CostRow
            title="Actual"
            subtitle={
              staffPlan?.name
                ? `Current staff plan: ${staffPlan.name}`
                : 'Current staff plan totals.'
            }
            breakdown={actual}
            accent="actual"
            deltas={{ total: totalΔ, onshore: onΔ, hub: hubΔ }}
          />
        )}
      </div>
    </section>
  );
}

function CostRow({
  title,
  subtitle,
  breakdown,
  accent,
  deltas,
}: {
  title: string;
  subtitle?: string;
  breakdown: CostBreakdown;
  accent: 'modeled' | 'actual';
  deltas?: { total: number; onshore: number; hub: number };
}) {
  const { money } = useCurrency();
  const total = breakdown.total;
  const on = breakdown.onshore;
  const hub = hubCost(breakdown);
  const hubFteTotal = hubFte(breakdown);

  const onPct = total > 0 ? on / total : 0;
  const nsPct = total > 0 ? breakdown.nearshore / total : 0;
  const offPct = total > 0 ? breakdown.offshore / total : 0;

  return (
    <div
      className={clsx(
        'rounded-lg border p-3',
        accent === 'modeled'
          ? 'border-brand-purple-100 bg-brand-purple-50/30'
          : 'border-slate-200 bg-white',
      )}
    >
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div>
          <div
            className={clsx(
              'text-[11px] uppercase tracking-wider font-semibold',
              accent === 'modeled' ? 'text-brand-purple-700' : 'text-slate-600',
            )}
          >
            {title}
          </div>
          {subtitle && <div className="text-[11px] text-slate-500">{subtitle}</div>}
        </div>
        <div
          className={clsx(
            'text-xl font-bold tabular-nums',
            accent === 'modeled' ? 'text-brand-purple-800' : 'text-slate-800',
          )}
        >
          {money(total)}
        </div>
      </div>
      {/* Stacked composition bar */}
      <div className="h-2 rounded-full overflow-hidden flex bg-slate-100 mb-3">
        <div
          className="bg-slate-400/80"
          style={{ width: `${onPct * 100}%` }}
          title={`Onshore ${(onPct * 100).toFixed(0)}%`}
        />
        <div
          className="bg-emerald-300"
          style={{ width: `${nsPct * 100}%` }}
          title={`Nearshore ${(nsPct * 100).toFixed(0)}%`}
        />
        <div
          className="bg-amber-300"
          style={{ width: `${offPct * 100}%` }}
          title={`Offshore ${(offPct * 100).toFixed(0)}%`}
        />
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Metric
          label="Onshore"
          dollars={on}
          fte={breakdown.fteOnshore}
          delta={deltas?.onshore}
          tone="on"
        />
        <Metric
          label="Hub"
          dollars={hub}
          fte={hubFteTotal}
          delta={deltas?.hub}
          tone="hub"
          emphasis
          sublabel={
            breakdown.fteNearshore + breakdown.fteOffshore > 0
              ? `${fteShare('Near', breakdown.fteNearshore, breakdown.nearshore, money)}${
                  breakdown.fteNearshore > 0 && breakdown.fteOffshore > 0 ? ' · ' : ''
                }${fteShare('Off', breakdown.fteOffshore, breakdown.offshore, money)}`
              : undefined
          }
        />
        <Metric
          label="Total FTE"
          fte={breakdown.fteTotal}
          tone="neutral"
          plain
        />
        <Metric
          label="Blended rate"
          dollars={blendedRate(breakdown)}
          tone="neutral"
          plain
          sublabel="per FTE / yr"
        />
      </div>
    </div>
  );
}

function fteShare(
  label: string,
  fte: number,
  dollars: number,
  money: (n: number) => string,
): string {
  if (fte <= 0) return '';
  return `${label} ${fte.toFixed(1)}·${money(dollars)}`;
}

function Metric({
  label,
  dollars,
  fte,
  delta,
  tone,
  emphasis,
  plain,
  sublabel,
}: {
  label: string;
  dollars?: number;
  fte?: number;
  delta?: number;
  tone: 'on' | 'hub' | 'neutral';
  emphasis?: boolean;
  plain?: boolean;
  sublabel?: string;
}) {
  const { money } = useCurrency();
  const bg =
    tone === 'hub'
      ? 'bg-amber-50/40 border-amber-200/70'
      : 'bg-white border-slate-200';
  const labelColor = tone === 'hub' ? 'text-amber-700' : 'text-slate-500';
  const valueColor = tone === 'hub' ? 'text-amber-900' : 'text-slate-800';

  return (
    <div
      className={clsx(
        'rounded-md border px-3 py-2',
        bg,
        emphasis && 'shadow-sm',
      )}
    >
      <div className={clsx('text-[10px] uppercase tracking-wider', labelColor)}>
        {label}
      </div>
      <div
        className={clsx(
          'tabular-nums',
          plain ? 'font-medium' : 'font-semibold',
          emphasis ? 'text-base' : 'text-sm',
          valueColor,
        )}
      >
        {dollars !== undefined ? money(dollars) : ''}
        {fte !== undefined && dollars === undefined && (
          <span>{fte.toFixed(1)}</span>
        )}
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-2 text-[11px] text-slate-500">
        <span>
          {fte !== undefined && dollars !== undefined && (
            <span className="tabular-nums">{fte.toFixed(1)} FTE</span>
          )}
          {sublabel && (fte === undefined || dollars === undefined) && (
            <span>{sublabel}</span>
          )}
          {sublabel && fte !== undefined && dollars !== undefined && (
            <span> · {sublabel}</span>
          )}
        </span>
        {typeof delta === 'number' && Math.abs(delta) > 0.5 && (
          <DeltaInline value={delta} />
        )}
      </div>
    </div>
  );
}

function DeltaInline({ value }: { value: number }) {
  const { money } = useCurrency();
  const positive = value > 0;
  if (Math.abs(value) < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 text-slate-400">
        <Minus size={10} className="opacity-60" /> 0
      </span>
    );
  }
  const Icon = positive ? ArrowUp : ArrowDown;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 font-medium',
        positive ? 'text-emerald-700' : 'text-rose-600',
      )}
    >
      <Icon size={10} />
      {positive ? '+' : ''}
      {money(value)}
    </span>
  );
}

function DeltaPill({
  label,
  value,
  modeled,
  actual,
}: {
  label: string;
  value: number;
  modeled: number;
  actual: number;
}) {
  const { money } = useCurrency();
  const negligible = Math.abs(value) < 1; // less than one currency unit
  const positive = value > 0;
  const tone = negligible
    ? 'border-slate-200 bg-white text-slate-500'
    : positive
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : 'border-rose-200 bg-rose-50 text-rose-800';

  return (
    <div
      className={clsx(
        'rounded-lg border px-3 py-1.5 flex flex-col items-end min-w-[180px]',
        tone,
      )}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="tabular-nums font-semibold text-lg">
        {negligible ? money(0) : `${positive ? '+' : ''}${money(value)}`}
      </div>
      <div className="text-[10px] opacity-70 tabular-nums">
        actual {money(actual)} · modeled {money(modeled)}
      </div>
    </div>
  );
}
